/**
 * Which channel process to open (A8b `q-channel-endpoint-and-redirect`, multi-core-world.md §5.3b).
 * With several channel processes behind one host, each answers its own `/ws/c<n>` (Caddy routes it);
 * the server says which `n` this login belongs on (`GET /api/world/channel`: the account's own live
 * channel, else by load). A server that does not answer — a single process (404), no live channel
 * (503), an older build, a network error, a slow reply — leaves the client on `/ws`, exactly as
 * before this file existed.
 */

import { serverHttpBase, serverWsUrl } from './platform.js';

/** How long the ask may take before the client opens `/ws` instead. */
export const CHANNEL_ASK_TIMEOUT_MS = 3000;

/** Pure: the channel's socket URL from the gameplay base URL (`…/ws` ⇒ `…/ws/c<n>`). */
export function channelWsUrl(baseWsUrl: string, channel: number): string {
  return `${baseWsUrl.replace(/\/+$/, '')}/c${channel}`;
}

/**
 * Pure: the socket URL a `redirect { channel }` sends the client to, from the URL the session was
 * opened on (A8b-2) — `…/ws` and `…/ws/c<m>` both become `…/ws/c<channel>`.
 */
export function redirectWsUrl(sessionUrl: string, channel: number): string {
  return channelWsUrl(sessionUrl.replace(/\/+$/, '').replace(/\/c\d+$/, ''), channel);
}

/** Pure: the channel in a `/api/world/channel` answer, or null for anything else. */
export function parseChannelAnswer(body: unknown): number | null {
  const channel = (body as { channel?: unknown } | null)?.channel;
  return typeof channel === 'number' && Number.isInteger(channel) && channel >= 1 && channel <= 99
    ? channel
    : null;
}

export interface ResolveOptions {
  readonly fetchImpl?: typeof fetch;
  readonly httpBase?: string;
  readonly baseWsUrl?: string;
  readonly timeoutMs?: number;
}

/**
 * Ask which channel to open; never rejects. Null when the server named no channel (any non-200,
 * a malformed body, a network error, a slow reply) — the caller decides what that means.
 */
export async function askChannelWsUrl(
  token: string,
  options: ResolveOptions = {},
): Promise<string | null> {
  const baseWsUrl = options.baseWsUrl ?? serverWsUrl();
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  if (typeof fetchImpl !== 'function') return null;
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), options.timeoutMs ?? CHANNEL_ASK_TIMEOUT_MS);
  try {
    const res = await fetchImpl(`${options.httpBase ?? serverHttpBase()}/api/world/channel`, {
      headers: { authorization: `Bearer ${token}` },
      signal: abort.signal,
    });
    if (!res.ok) return null;
    const channel = parseChannelAnswer(await res.json());
    return channel === null ? null : channelWsUrl(baseWsUrl, channel);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Ask which channel to open for a hello; every failure is the plain `/ws` URL. */
export async function resolveGameWsUrl(
  token: string,
  options: ResolveOptions = {},
): Promise<string> {
  return (await askChannelWsUrl(token, options)) ?? options.baseWsUrl ?? serverWsUrl();
}

/**
 * The connection's two socket-URL hooks for a login. A guest demo session never asks: the guest
 * origin runs one process with no channels, so the ask was a 404 on every demo visit (a red
 * console line and a wasted round trip before the socket opened). It opens `/ws` at once.
 */
export function channelHooks(
  token: string,
  options: { readonly guest: boolean } & ResolveOptions,
): {
  readonly helloUrl: () => string | Promise<string>;
  readonly channelAsk: (() => Promise<string | null>) | undefined;
} {
  if (options.guest)
    return { helloUrl: () => options.baseWsUrl ?? serverWsUrl(), channelAsk: undefined };
  return {
    helloUrl: () => resolveGameWsUrl(token, options),
    channelAsk: () => askChannelWsUrl(token, options),
  };
}
